import React, { useState } from "react";
import MenuBar from "./common/MenuBar";
import { authAPI, projectAPI } from "../../../../../services/api";
import { formAPI } from "../../../../../services/api";

// API 엔드포인트 매핑 테이블
const API_ENDPOINTS = {
  BASE_URL: "https://bap7u9i7ig.execute-api.ap-northeast-2.amazonaws.com/prod",
  ENDPOINTS: {
    PROJECTS: {
      CREATE: "/api/projects/create",
      LIST: "/api/projects",
    },
    FORM_VERSIONS: {
      LIST: "/form-versions",
      DETAIL: (versionId) => `/form-versions/${versionId}`,
    },
    CASES: {
      DETAIL: (caseId) => `/cases/${caseId}`,
    },
    FORMS: {
      CREATE: "/forms",
    },
    AUTH: {
      USER_INFO: "/auth/me",
    },
    DB: {
      CHECK: "/db-check",
    },
  },
};

const APITest = () => {
  const [formTestResults, setFormTestResults] = useState(null);
  const [dbTestResults, setDbTestResults] = useState(null);
  const [caseTestResults, setCaseTestResults] = useState(null);
  const [caseId, setCaseId] = useState("25"); // 기본값 설정
  const [group_id, setGroup_id] = useState("5"); // group_id 입력 필드 추가
  const [template_id, setTemplate_id] = useState("2"); // template_id 입력 필드 추가
  const [userInfoResults, setUserInfoResults] = useState(null);
  const [projectTestResults, setProjectTestResults] = useState(null); // 프로젝트 생성 테스트 결과
  const [projectData, setProjectData] = useState({
    name: "",
    description: "",
    projectType: "eCRF",
  });
  const [formVersionsResults, setFormVersionsResults] = useState(null);
  const [projectId, setProjectId] = useState("");
  const [formDataTestResults, setFormDataTestResults] = useState(null);
  const [formDataId, setFormDataId] = useState("");

  // 프로젝트 데이터 입력 변경 처리
  const handleProjectInputChange = (e) => {
    const { name, value } = e.target;
    setProjectData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // API 요청 헬퍼 함수
  const makeRequest = async (endpoint, method = "GET", body = null) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("토큰이 없습니다. 먼저 로그인해주세요.");
      }

      const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      };

      const config = {
        method,
        headers,
        credentials: "include",
        ...(body && { body: JSON.stringify(body) }),
      };

      const response = await fetch(
        `${API_ENDPOINTS.BASE_URL}${endpoint}`,
        config
      );

      if (!response.ok) {
        throw new Error(`서버 응답 오류: ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      throw error;
    }
  };

  // 프로젝트 생성 API 테스트
  const testCreateProject = async () => {
    try {
      const response = await makeRequest(
        API_ENDPOINTS.ENDPOINTS.PROJECTS.CREATE,
        "POST",
        projectData
      );
      setProjectTestResults(response);
    } catch (error) {
      setProjectTestResults({
        error: error.message,
        networkError: error.name === "AxiosError",
        details: error.response?.data,
      });
    }
  };

  // 폼 버전 목록 조회 테스트
  const testGetFormVersions = async () => {
    try {
      const response = await makeRequest(
        `${API_ENDPOINTS.ENDPOINTS.FORM_VERSIONS.LIST}?project_id=${projectId}`
      );
      setFormVersionsResults(response);
    } catch (error) {
      setFormVersionsResults({
        error: error.message,
        details: error.response?.data,
      });
    }
  };

  // 케이스 조회 테스트
  const testGetCase = async () => {
    try {
      const response = await makeRequest(
        API_ENDPOINTS.ENDPOINTS.CASES.DETAIL(caseId)
      );
      setCaseTestResults(response);
    } catch (error) {
      setCaseTestResults({ error: error.message });
    }
  };

  // 폼 생성 테스트
  const testPostForm = async () => {
    try {
      const testData = {
        group_id: parseInt(group_id),
        template_id: parseInt(template_id),
        formData: {
          pt_name: "테스트 환자",
          pt_number: "TEST_001",
          birth: "1990-01-01",
          sex: "Male",
          height: "170",
          weight: "70",
          waist_circumference: "80",
          hip_circumference: "90",
          ED_arrival_time: "2024-03-20T10:00:00",
          transfer: "No",
          transfer_hospital: "",
          pre_cpr: "No",
          pre_defibrillation: "No",
          arrest_location: "Home",
          arrest_location_others: "",
          Baseline_CPC: "1",
          witness: "Yes",
          arrest_time: "2024-03-20T09:30:00",
          LNT_time: "2024-03-20T09:25:00",
          witness_by: "Family",
          status: "In Progress",
        },
      };

      const response = await makeRequest(
        API_ENDPOINTS.ENDPOINTS.FORMS.CREATE,
        "POST",
        testData
      );
      setFormTestResults(response);
    } catch (error) {
      setFormTestResults({ error: error.message });
    }
  };

  // DB 체크 테스트
  const testDbCheck = async () => {
    try {
      const response = await makeRequest(API_ENDPOINTS.ENDPOINTS.DB.CHECK);
      setDbTestResults(response);
    } catch (error) {
      setDbTestResults({ error: error.message });
    }
  };

  // DB 체크 POST 테스트
  const testDbCheckPost = async () => {
    try {
      const response = await makeRequest(
        API_ENDPOINTS.ENDPOINTS.DB.CHECK,
        "POST",
        {}
      );
      setDbTestResults(response);
    } catch (error) {
      setDbTestResults({ error: error.message });
    }
  };

  // 사용자 정보 조회 테스트
  const testGetUserInfo = async () => {
    try {
      const response = await makeRequest(
        API_ENDPOINTS.ENDPOINTS.AUTH.USER_INFO
      );
      setUserInfoResults(response);
    } catch (error) {
      setUserInfoResults({
        error: error.message,
        details: error.response?.data,
      });
    }
  };

  // get-formdata 테스트
  const testGetFormData = async () => {
    try {
      const response = await formAPI.getFormDataById(formDataId);
      setFormDataTestResults(response);
    } catch (error) {
      setFormDataTestResults({ error: error.message });
    }
  };

  const copyToClipboard = (data) => {
    navigator.clipboard
      .writeText(JSON.stringify(data, null, 2))
      .then(() => alert("결과가 클립보드에 복사되었습니다."))
      .catch((err) => console.error("복사 실패:", err));
  };

  const ResultMonitor = ({ data, title }) => (
    <div style={{ position: "relative" }}>
      <pre
        style={{
          backgroundColor: "#f5f5f5",
          padding: "15px",
          borderRadius: "4px",
          overflow: "auto",
          minHeight: "100px",
          marginTop: "10px",
        }}
      >
        {data ? JSON.stringify(data, null, 2) : "결과가 여기에 표시됩니다..."}
      </pre>
      {data && (
        <button
          onClick={() => copyToClipboard(data)}
          style={{
            position: "absolute",
            top: "20px",
            right: "20px",
            padding: "5px 10px",
            backgroundColor: "#666",
            color: "white",
            border: "none",
            borderRadius: "3px",
            cursor: "pointer",
          }}
        >
          Copy
        </button>
      )}
    </div>
  );

  return (
    <div>
      <MenuBar />
      <div className="api-test-container" style={{ padding: "20px" }}>
        <h2>API 테스트</h2>

        <div className="test-section" style={{ marginBottom: "40px" }}>
          <h3>POST /api/projects/create 테스트</h3>
          <div style={{ marginBottom: "10px" }}>
            <input
              type="text"
              name="name"
              value={projectData.name}
              onChange={handleProjectInputChange}
              placeholder="프로젝트 이름 (필수)"
              style={{
                padding: "8px",
                marginRight: "10px",
                width: "200px",
              }}
            />
            <input
              type="text"
              name="description"
              value={projectData.description}
              onChange={handleProjectInputChange}
              placeholder="프로젝트 설명 (선택)"
              style={{
                padding: "8px",
                marginRight: "10px",
                width: "200px",
              }}
            />
            <select
              name="projectType"
              value={projectData.projectType}
              onChange={handleProjectInputChange}
              style={{
                padding: "8px",
                marginRight: "10px",
                width: "200px",
              }}
            >
              <option value="eCRF">eCRF (임상시험)</option>
              <option value="KTAS">KTAS (응급 중증도 분류)</option>
              <option value="Note">연구노트</option>
            </select>
            <button
              onClick={testCreateProject}
              style={{
                padding: "10px 20px",
                backgroundColor: "#4CAF50",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              프로젝트 생성 테스트
            </button>
          </div>
          <ResultMonitor
            data={projectTestResults}
            title="POST /api/projects/create 결과"
          />
        </div>

        <div className="test-section" style={{ marginBottom: "40px" }}>
          <h3>GET /cases/{caseId} 테스트</h3>
          <div style={{ marginBottom: "10px" }}>
            <input
              type="text"
              value={caseId}
              onChange={(e) => setCaseId(e.target.value)}
              placeholder="Case ID 입력"
              style={{
                padding: "8px",
                marginRight: "10px",
                width: "200px",
              }}
            />
            <button
              onClick={testGetCase}
              style={{
                padding: "10px 20px",
                backgroundColor: "#4CAF50",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              테스트 실행
            </button>
          </div>
          <ResultMonitor data={caseTestResults} title="GET /cases 결과" />
        </div>

        <div className="test-section" style={{ marginBottom: "40px" }}>
          <h3>POST /forms 테스트</h3>
          <div style={{ marginBottom: "10px" }}>
            <input
              type="text"
              value={group_id}
              onChange={(e) => setGroup_id(e.target.value)}
              placeholder="Group ID 입력"
              style={{
                padding: "8px",
                marginRight: "10px",
                width: "200px",
              }}
            />
            <input
              type="text"
              value={template_id}
              onChange={(e) => setTemplate_id(e.target.value)}
              placeholder="Template ID 입력"
              style={{
                padding: "8px",
                marginRight: "10px",
                width: "200px",
              }}
            />
            <button
              onClick={testPostForm}
              style={{
                padding: "10px 20px",
                backgroundColor: "#4CAF50",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              테스트 실행
            </button>
          </div>
          <ResultMonitor data={formTestResults} title="POST /forms 결과" />
        </div>

        <div className="test-section">
          <h3>DB 테스트</h3>
          <div style={{ display: "flex", gap: "10px", marginBottom: "20px" }}>
            <button
              onClick={testDbCheck}
              style={{
                padding: "10px 20px",
                backgroundColor: "#2196F3",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              DB 연결 확인
            </button>
            <button
              onClick={testDbCheckPost}
              style={{
                padding: "10px 20px",
                backgroundColor: "#FF9800",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              테스트 데이터 생성
            </button>
          </div>
          <ResultMonitor data={dbTestResults} title="DB 테스트 결과" />
        </div>

        <div className="test-section" style={{ marginBottom: "40px" }}>
          <h3>GET /auth/me 테스트</h3>
          <div style={{ marginBottom: "10px" }}>
            <button
              onClick={testGetUserInfo}
              style={{
                padding: "10px 20px",
                backgroundColor: "#2196F3",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              사용자 정보 조회
            </button>
          </div>
          <ResultMonitor data={userInfoResults} title="GET /auth/me 결과" />
        </div>

        <div className="test-section" style={{ marginBottom: "40px" }}>
          <h3>GET /form-versions 테스트</h3>
          <div style={{ marginBottom: "10px" }}>
            <input
              type="text"
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              placeholder="Project ID 입력"
              style={{
                padding: "8px",
                marginRight: "10px",
                width: "200px",
              }}
            />
            <button
              onClick={testGetFormVersions}
              style={{
                padding: "10px 20px",
                backgroundColor: "#4CAF50",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              폼 버전 목록 조회
            </button>
          </div>
          <ResultMonitor
            data={formVersionsResults}
            title="GET /form-versions 결과"
          />
        </div>

        <div className="test-section" style={{ marginBottom: "40px" }}>
          <h3>GET /edc/get-formdata 테스트</h3>
          <div style={{ marginBottom: "10px" }}>
            <input
              type="text"
              value={formDataId}
              onChange={(e) => setFormDataId(e.target.value)}
              placeholder="dataId 입력"
              style={{
                padding: "8px",
                marginRight: "10px",
                width: "200px",
              }}
            />
            <button
              onClick={testGetFormData}
              style={{
                padding: "10px 20px",
                backgroundColor: "#4CAF50",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              테스트 실행
            </button>
          </div>
          <ResultMonitor
            data={formDataTestResults}
            title="GET /edc/get-formdata 결과"
          />
        </div>
      </div>
    </div>
  );
};

export default APITest;
